/**
 * What signing out takes with it, and the three keys it must not.
 *
 * The logout button people actually press (shared/components/AppHeader.jsx — components/Navigation.jsx
 * has no importers) used to call `localStorage.clear()`. That is the right DEFAULT and it stays: a
 * construction office shares laptops and tablets, and the next person to sign in must not inherit the
 * last one's cached user, session flags or bid drafts (those carry confidential pricing — see the
 * F-L4 note in useAuth.logout, which removes the same things by an explicit list). A denylist would
 * leak the next key somebody adds; a clear() cannot.
 *
 * ── SO THE RULE IS ABOUT WHOSE KEY IT IS, NOT WHAT IT CONTAINS ───────────────────────────────────
 * A key that describes THE PERSON goes. A key that describes THIS DEVICE or THIS HOST stays, because
 * nothing about it changed when somebody left:
 *
 *   * `tynvara.lang` — the choice made with the "English / Español" toggle on the sign-in page. It is
 *     rung (b), defined as "remembered in that browser", and clearing it made that definition false:
 *     a Spanish-reading foreman on a shop tablet that reports en-US re-chose his language at the
 *     start of every shift, with nothing telling him why. It is not personal data — it is two words
 *     about how this screen is legible.
 *   * `tynvara.publicInspection.lang` — the same thing, chosen by an anonymous hand at a QR sticker.
 *     The person signing out never owned it.
 *   * `tenant_branding` — host-resolved, identical for everybody who reaches this origin, and the
 *     carrier of rung (d), the company default. Clearing it made every post-logout sign-in page paint
 *     English first and swap to Spanish when the branding fetch landed. localStorage is already
 *     origin-scoped and every tenant has its own host, so this cache can never describe a tenant
 *     other than the one whose page is loading.
 *
 * ── AND THEN TELL THE LANGUAGE PROVIDER ──────────────────────────────────────────────────────────
 * Sign-out is an SPA route change, so nothing remounts and nothing re-reads storage on its own. The
 * signal is what makes the sign-in page stop rendering in the departed user's language. Without it
 * the toggle is inert at the exact moment somebody who cannot read the page needs it.
 *
 * Tour progress (`bfs_tour_*`) is deliberately NOT kept: it is per-user by key, so it is exactly the
 * kind of thing the clear() is for. shared/tour/tourStorage.js documents that trade-off; it is
 * unchanged by this file.
 */
import { clearAccessToken } from '../api/tokenStore'
import { TENANT_BRANDING_STORAGE_KEY } from '../../config/config.js'
import { accountLanguageChanged } from '../i18n/accountLanguage'
import { LANGUAGE_STORAGE_KEY, PUBLIC_LANGUAGE_STORAGE_KEY } from '../i18n/languages'

/** Keys that describe the device or the host rather than the person who just left. */
export const KEPT_THROUGH_SIGN_OUT = Object.freeze([
  LANGUAGE_STORAGE_KEY,
  PUBLIC_LANGUAGE_STORAGE_KEY,
  TENANT_BRANDING_STORAGE_KEY,
])

/**
 * Clear this browser of the person who just signed out, and tell the app the session changed.
 *
 * Storage can throw outright (Safari private mode, storage disabled by policy) and a sign-out must
 * complete anyway — being unable to leave is worse than a stale cache. The signal fires regardless,
 * so the screen still stops speaking for somebody who is gone.
 */
export function signOutStorage() {
  // THE CREDENTIAL FIRST, and before anything that can throw.
  //
  // The access token does NOT live in localStorage -- it is a module-level variable in
  // shared/api/tokenStore.js, deliberately, so an XSS cannot exfiltrate it (Phase B3). The
  // consequence is that `localStorage.clear()` below does not touch it, and this function was the
  // whole of what the logout button did: AppHeader.jsx calls signOutStorage() rather than
  // useAuth.logout(), and it is by its own comment the only logout button in the application.
  //
  // So the token survived sign-out, in memory, for the rest of its 15-minute life
  // (identity/config/settings.py ACCESS_TOKEN_EXPIRE_MINUTES). And it stayed USABLE: the shared
  // request path performs no revocation check -- shared/auth/dependencies.py says so in a TODO,
  // because a live check would cost a DB round-trip per request across all 15 services. The backend
  // logout call therefore revokes a token that nothing subsequently asks about.
  //
  // Net effect on a shared office laptop: sign out, press Back, and the next person had a working
  // authenticated session -- not merely stale screens -- until the token aged out.
  //
  // Cleared before the try/catch because storage can throw outright (Safari private mode, storage
  // disabled by policy) and the one thing that must not be skipped when it does is dropping the
  // credential.
  clearAccessToken()
  try {
    const kept = KEPT_THROUGH_SIGN_OUT
      .map((key) => [key, window.localStorage.getItem(key)])
      .filter(([, value]) => value !== null)
    window.localStorage.clear()
    kept.forEach(([key, value]) => window.localStorage.setItem(key, value))
  } catch (err) { /* see above */ }
  accountLanguageChanged()
}
